// Whether.markets — Market Product Configuration
// Maps every CME weather derivative to a Whether perp product

export type ProductType = 'hdd' | 'cdd' | 'temp' | 'rainfall' | 'snowfall' | 'hurricane' | 'wind' | 'drought' | 'gas' | 'oil' | 'carbon' | 'power' | 'spread' | 'freight' | 'product';
export type Region = 'us' | 'europe' | 'canada' | 'apac' | 'global' | 'middle_east';
export type LaunchPhase = 1 | 2 | 3 | 4;

export interface StationConfig {
  id: string;           // ICAO code
  name: string;
  city: string;
  country: string;
  region: Region;
  lat: number;
  lon: number;
  timezone: string;
  elevation_ft: number;
}

export interface MarketConfig {
  id: string;            // e.g., "hdd-nyc-7d"
  name: string;          // e.g., "NYC Heating Degree Days (7D)"
  shortName: string;     // e.g., "NYC HDD"
  type: ProductType;
  station: StationConfig;
  indexFormula: string;
  windowDays: number;
  resolutionFreq: 'daily' | '6h' | 'weekly';
  maxLeverage: number;
  mmrBps: number;
  fundingCapBps: number;
  tickValue: string;     // in TON
  referenceTemp: number; // 65°F for US, 18°C for Europe
  unit: '°F' | '°C' | 'in' | 'mm' | 'mph' | 'kt' | 'index';
  seasonal: boolean;
  activeSeason?: string; // e.g., "Oct-Apr" for HDD
  launchPhase: LaunchPhase;
  oracleSource: string;
  cmeParity: string;     // CME product code reference
}

// ==================== STATIONS ====================

export const US_STATIONS: StationConfig[] = [
  { id: 'KATL', name: 'Hartsfield-Jackson', city: 'Atlanta', country: 'US', region: 'us', lat: 33.6407, lon: -84.4277, timezone: 'America/New_York', elevation_ft: 1026 },
  { id: 'KBWI', name: 'Baltimore-Washington', city: 'Baltimore', country: 'US', region: 'us', lat: 39.1754, lon: -76.6684, timezone: 'America/New_York', elevation_ft: 146 },
  { id: 'KBOS', name: 'Logan International', city: 'Boston', country: 'US', region: 'us', lat: 42.3656, lon: -71.0096, timezone: 'America/New_York', elevation_ft: 20 },
  { id: 'KORD', name: "O'Hare International", city: 'Chicago', country: 'US', region: 'us', lat: 41.9742, lon: -87.9073, timezone: 'America/Chicago', elevation_ft: 672 },
  { id: 'KCVG', name: 'Cincinnati/Northern KY', city: 'Cincinnati', country: 'US', region: 'us', lat: 39.0488, lon: -84.6678, timezone: 'America/New_York', elevation_ft: 896 },
  { id: 'KCOS', name: 'City of Colorado Springs', city: 'Colorado Springs', country: 'US', region: 'us', lat: 38.8058, lon: -104.7003, timezone: 'America/Denver', elevation_ft: 6187 },
  { id: 'KDFW', name: 'Dallas/Fort Worth', city: 'Dallas', country: 'US', region: 'us', lat: 32.8998, lon: -97.0403, timezone: 'America/Chicago', elevation_ft: 607 },
  { id: 'KDSM', name: 'Des Moines International', city: 'Des Moines', country: 'US', region: 'us', lat: 41.5341, lon: -93.6631, timezone: 'America/Chicago', elevation_ft: 958 },
  { id: 'KDTW', name: 'Detroit Metropolitan', city: 'Detroit', country: 'US', region: 'us', lat: 42.2162, lon: -83.3554, timezone: 'America/Detroit', elevation_ft: 645 },
  { id: 'KIAH', name: 'George Bush Intercontinental', city: 'Houston', country: 'US', region: 'us', lat: 29.9844, lon: -95.3414, timezone: 'America/Chicago', elevation_ft: 97 },
  { id: 'KJAX', name: 'Jacksonville International', city: 'Jacksonville', country: 'US', region: 'us', lat: 30.4941, lon: -81.6879, timezone: 'America/New_York', elevation_ft: 30 },
  { id: 'KMCI', name: 'Kansas City International', city: 'Kansas City', country: 'US', region: 'us', lat: 39.2976, lon: -94.7139, timezone: 'America/Chicago', elevation_ft: 1026 },
  { id: 'KLAS', name: 'Harry Reid International', city: 'Las Vegas', country: 'US', region: 'us', lat: 36.0840, lon: -115.1537, timezone: 'America/Los_Angeles', elevation_ft: 2181 },
  { id: 'KLAX', name: 'Los Angeles International', city: 'Los Angeles', country: 'US', region: 'us', lat: 33.9425, lon: -118.4081, timezone: 'America/Los_Angeles', elevation_ft: 128 },
  { id: 'KMIA', name: 'Miami International', city: 'Miami', country: 'US', region: 'us', lat: 25.7959, lon: -80.2870, timezone: 'America/New_York', elevation_ft: 8 },
  { id: 'KMSP', name: 'Minneapolis-Saint Paul', city: 'Minneapolis', country: 'US', region: 'us', lat: 44.8848, lon: -93.2223, timezone: 'America/Chicago', elevation_ft: 841 },
  { id: 'KJFK', name: 'John F. Kennedy', city: 'New York', country: 'US', region: 'us', lat: 40.6413, lon: -73.7781, timezone: 'America/New_York', elevation_ft: 13 },
  { id: 'KPHL', name: 'Philadelphia International', city: 'Philadelphia', country: 'US', region: 'us', lat: 39.8721, lon: -75.2411, timezone: 'America/New_York', elevation_ft: 36 },
  { id: 'KPDX', name: 'Portland International', city: 'Portland', country: 'US', region: 'us', lat: 45.5898, lon: -122.5951, timezone: 'America/Los_Angeles', elevation_ft: 31 },
  { id: 'KRDU', name: 'Raleigh-Durham International', city: 'Raleigh', country: 'US', region: 'us', lat: 35.8776, lon: -78.7875, timezone: 'America/New_York', elevation_ft: 435 },
  { id: 'KSMF', name: 'Sacramento International', city: 'Sacramento', country: 'US', region: 'us', lat: 38.6955, lon: -121.5908, timezone: 'America/Los_Angeles', elevation_ft: 27 },
  { id: 'KSLC', name: 'Salt Lake City International', city: 'Salt Lake City', country: 'US', region: 'us', lat: 40.7884, lon: -111.9778, timezone: 'America/Denver', elevation_ft: 4227 },
  { id: 'KSEA', name: 'Seattle-Tacoma', city: 'Seattle', country: 'US', region: 'us', lat: 47.4502, lon: -122.3088, timezone: 'America/Los_Angeles', elevation_ft: 433 },
  { id: 'KDCA', name: 'Ronald Reagan National', city: 'Washington DC', country: 'US', region: 'us', lat: 38.8512, lon: -77.0402, timezone: 'America/New_York', elevation_ft: 15 },
];

export const EUROPE_STATIONS: StationConfig[] = [
  { id: 'EHAM', name: 'Schiphol', city: 'Amsterdam', country: 'NL', region: 'europe', lat: 52.3105, lon: 4.7683, timezone: 'Europe/Amsterdam', elevation_ft: -11 },
  { id: 'LEBL', name: 'El Prat', city: 'Barcelona', country: 'ES', region: 'europe', lat: 41.2974, lon: 2.0833, timezone: 'Europe/Madrid', elevation_ft: 12 },
  { id: 'EDDB', name: 'Berlin Brandenburg', city: 'Berlin', country: 'DE', region: 'europe', lat: 52.3667, lon: 13.5033, timezone: 'Europe/Berlin', elevation_ft: 157 },
  { id: 'EDZE', name: 'Essen/Mülheim', city: 'Essen', country: 'DE', region: 'europe', lat: 51.4023, lon: 6.9372, timezone: 'Europe/Berlin', elevation_ft: 424 },
  { id: 'EGLL', name: 'Heathrow', city: 'London', country: 'GB', region: 'europe', lat: 51.4700, lon: -0.4543, timezone: 'Europe/London', elevation_ft: 83 },
  { id: 'LEMD', name: 'Barajas', city: 'Madrid', country: 'ES', region: 'europe', lat: 40.4719, lon: -3.5626, timezone: 'Europe/Madrid', elevation_ft: 2000 },
  { id: 'ENGM', name: 'Gardermoen', city: 'Oslo', country: 'NO', region: 'europe', lat: 60.1939, lon: 11.1004, timezone: 'Europe/Oslo', elevation_ft: 689 },
  { id: 'LFPG', name: 'Charles de Gaulle', city: 'Paris', country: 'FR', region: 'europe', lat: 49.0097, lon: 2.5479, timezone: 'Europe/Paris', elevation_ft: 392 },
  { id: 'LKPR', name: 'Václav Havel', city: 'Prague', country: 'CZ', region: 'europe', lat: 50.1008, lon: 14.2600, timezone: 'Europe/Prague', elevation_ft: 1247 },
  { id: 'LIRF', name: 'Fiumicino', city: 'Rome', country: 'IT', region: 'europe', lat: 41.8003, lon: 12.2389, timezone: 'Europe/Rome', elevation_ft: 14 },
  { id: 'ESSA', name: 'Arlanda', city: 'Stockholm', country: 'SE', region: 'europe', lat: 59.6519, lon: 17.9186, timezone: 'Europe/Stockholm', elevation_ft: 137 },
];

export const CANADA_STATIONS: StationConfig[] = [
  { id: 'CYYC', name: 'Calgary International', city: 'Calgary', country: 'CA', region: 'canada', lat: 51.1215, lon: -114.0076, timezone: 'America/Edmonton', elevation_ft: 3557 },
  { id: 'CYEG', name: 'Edmonton International', city: 'Edmonton', country: 'CA', region: 'canada', lat: 53.3097, lon: -113.5800, timezone: 'America/Edmonton', elevation_ft: 2373 },
  { id: 'CYUL', name: 'Montréal-Trudeau', city: 'Montreal', country: 'CA', region: 'canada', lat: 45.4706, lon: -73.7408, timezone: 'America/Toronto', elevation_ft: 118 },
  { id: 'CYYZ', name: 'Toronto Pearson', city: 'Toronto', country: 'CA', region: 'canada', lat: 43.6777, lon: -79.6248, timezone: 'America/Toronto', elevation_ft: 569 },
  { id: 'CYVR', name: 'Vancouver International', city: 'Vancouver', country: 'CA', region: 'canada', lat: 49.1967, lon: -123.1815, timezone: 'America/Vancouver', elevation_ft: 14 },
  { id: 'CYWG', name: 'Winnipeg James Armstrong', city: 'Winnipeg', country: 'CA', region: 'canada', lat: 49.9100, lon: -97.2399, timezone: 'America/Winnipeg', elevation_ft: 783 },
];

export const APAC_STATIONS: StationConfig[] = [
  { id: 'RJTT', name: 'Haneda', city: 'Tokyo', country: 'JP', region: 'apac', lat: 35.5494, lon: 139.7798, timezone: 'Asia/Tokyo', elevation_ft: 35 },
  { id: 'RJOO', name: 'Itami', city: 'Osaka', country: 'JP', region: 'apac', lat: 34.7855, lon: 135.4380, timezone: 'Asia/Tokyo', elevation_ft: 39 },
  { id: 'RJOA', name: 'Hiroshima', city: 'Hiroshima', country: 'JP', region: 'apac', lat: 34.4361, lon: 132.9194, timezone: 'Asia/Tokyo', elevation_ft: 1088 },
  { id: 'YMML', name: 'Tullamarine', city: 'Melbourne', country: 'AU', region: 'apac', lat: -37.6690, lon: 144.8410, timezone: 'Australia/Melbourne', elevation_ft: 434 },
  { id: 'YSSY', name: 'Kingsford Smith', city: 'Sydney', country: 'AU', region: 'apac', lat: -33.9461, lon: 151.1772, timezone: 'Australia/Sydney', elevation_ft: 21 },
  { id: 'YBBN', name: 'Brisbane Airport', city: 'Brisbane', country: 'AU', region: 'apac', lat: -27.3842, lon: 153.1175, timezone: 'Australia/Brisbane', elevation_ft: 13 },
];

export const ALL_STATIONS = [...US_STATIONS, ...EUROPE_STATIONS, ...CANADA_STATIONS, ...APAC_STATIONS];

// ==================== PRODUCT TYPE CONFIGS ====================

interface ProductTypeConfig {
  type: ProductType;
  nameTemplate: string;
  shortTemplate: string;
  formula: string;
  defaultWindow: number;
  resolution: 'daily' | '6h' | 'weekly';
  maxLeverage: number;
  mmrBps: number;
  fundingCapBps: number;
  unit: '°F' | '°C' | 'in' | 'mm' | 'mph' | 'kt' | 'index';
  seasonal: boolean;
  activeSeason?: string;
  oracleSource: string;
}

const PRODUCT_TYPES: Record<string, ProductTypeConfig> = {
  hdd_us: {
    type: 'hdd', nameTemplate: '{city} HDD (7D Rolling)', shortTemplate: '{city} HDD',
    formula: 'max(0, 65 - Tavg)', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 10, mmrBps: 500, fundingCapBps: 10, unit: '°F',
    seasonal: true, activeSeason: 'Oct-Apr', oracleSource: 'NOAA GHCN-d'
  },
  cdd_us: {
    type: 'cdd', nameTemplate: '{city} CDD (7D Rolling)', shortTemplate: '{city} CDD',
    formula: 'max(0, Tavg - 65)', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 10, mmrBps: 500, fundingCapBps: 10, unit: '°F',
    seasonal: true, activeSeason: 'May-Sep', oracleSource: 'NOAA GHCN-d'
  },
  temp_us: {
    type: 'temp', nameTemplate: '{city} Temperature (7D Avg)', shortTemplate: '{city} TEMP',
    formula: '(Tmax + Tmin) / 2', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 20, mmrBps: 300, fundingCapBps: 10, unit: '°F',
    seasonal: false, oracleSource: 'NOAA GHCN-d'
  },
  hdd_eu: {
    type: 'hdd', nameTemplate: '{city} HDD (7D Rolling)', shortTemplate: '{city} HDD',
    formula: 'max(0, 18 - Tavg)', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 10, mmrBps: 500, fundingCapBps: 10, unit: '°C',
    seasonal: true, activeSeason: 'Oct-Mar', oracleSource: 'ECMWF/National Met'
  },
  cdd_eu: {
    type: 'cdd', nameTemplate: '{city} CDD (7D Rolling)', shortTemplate: '{city} CDD',
    formula: 'max(0, Tavg - 18)', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 10, mmrBps: 500, fundingCapBps: 10, unit: '°C',
    seasonal: true, activeSeason: 'Jun-Aug', oracleSource: 'ECMWF/National Met'
  },
  temp_eu: {
    type: 'temp', nameTemplate: '{city} Temperature (7D Avg)', shortTemplate: '{city} TEMP',
    formula: '(Tmax + Tmin) / 2', defaultWindow: 7, resolution: 'daily',
    maxLeverage: 20, mmrBps: 300, fundingCapBps: 10, unit: '°C',
    seasonal: false, oracleSource: 'ECMWF/National Met'
  },
};

// ==================== MARKET GENERATOR ====================

function generateMarketsForStation(station: StationConfig): MarketConfig[] {
  const isUS = station.region === 'us';
  const isCanada = station.region === 'canada';
  const isEurope = station.region === 'europe';
  const phase: LaunchPhase = isUS
    ? ([ 'KJFK','KORD','KDFW','KIAH','KATL','KMIA','KLAX','KSEA','KBOS','KDCA'].includes(station.id) ? 1 : 2)
    : isCanada ? 2 : isEurope ? 3 : 4;

  const suffix = isUS || isCanada ? 'us' : 'eu';
  const products = [
    PRODUCT_TYPES[`hdd_${suffix}`],
    PRODUCT_TYPES[`cdd_${suffix}`],
    PRODUCT_TYPES[`temp_${suffix}`],
  ];

  return products.map(p => ({
    id: `${p.type}-${station.city.toLowerCase().replace(/\s+/g, '-')}-${p.defaultWindow}d`,
    name: p.nameTemplate.replace('{city}', station.city),
    shortName: p.shortTemplate.replace('{city}', station.city),
    type: p.type,
    station,
    indexFormula: p.formula,
    windowDays: p.defaultWindow,
    resolutionFreq: p.resolution,
    maxLeverage: p.maxLeverage,
    mmrBps: p.mmrBps,
    fundingCapBps: p.fundingCapBps,
    tickValue: '1',
    referenceTemp: isUS || isCanada ? 65 : 18,
    unit: p.unit,
    seasonal: p.seasonal,
    activeSeason: p.activeSeason,
    launchPhase: phase,
    oracleSource: p.oracleSource,
    cmeParity: `CME ${p.type.toUpperCase()} ${station.city}`,
  }));
}

// Generate all 168 markets

// ==================== ENERGY MARKETS ====================

const VIRTUAL_STATION: StationConfig = {
  id: 'GLOBAL', name: 'Global', city: 'Global', country: 'GLOBAL',
  region: 'global', lat: 0, lon: 0, timezone: 'UTC', elevation_ft: 0,
};

function eStation(city: string, country: string, region: Region): StationConfig {
  return { ...VIRTUAL_STATION, city, country, region };
}

function eMkt(id: string, name: string, shortName: string, type: ProductType, city: string, country: string, region: Region, formula: string, oracle: string, cme: string, phase: LaunchPhase = 1, lev: number = 20): MarketConfig {
  return { id, name, shortName, type, station: eStation(city, country, region), indexFormula: formula, windowDays: 1, resolutionFreq: 'daily', maxLeverage: lev, mmrBps: 50, fundingCapBps: 75, tickValue: '0.01', referenceTemp: 0, unit: '\u00b0F', seasonal: false, launchPhase: phase, oracleSource: oracle, cmeParity: cme };
}

const ENERGY_MARKETS: MarketConfig[] = [
  // Natural Gas
  eMkt('natgas-hh', 'Henry Hub Natural Gas', 'HH Gas', 'gas', 'Henry Hub', 'US', 'us', 'NYMEX NG front month', 'EIA + Yahoo', 'NG'),
  eMkt('natgas-ttf', 'TTF European Gas', 'TTF Gas', 'gas', 'TTF', 'NL', 'europe', 'ICE TTF front month', 'Yahoo Finance', 'TTF'),
  eMkt('natgas-jkm', 'JKM Asian LNG', 'JKM LNG', 'gas', 'JKM', 'JP', 'apac', 'Platts JKM', 'Yahoo Finance', 'JKM', 2, 15),
  eMkt('natgas-aeco', 'AECO Canadian Gas', 'AECO', 'gas', 'Alberta', 'CA', 'canada', 'AECO-C Hub', 'Estimate', 'AECO', 2, 15),
  eMkt('natgas-nbp', 'NBP UK Gas', 'NBP', 'gas', 'NBP', 'GB', 'europe', 'ICE NBP front month', 'Estimate', 'NBP', 2, 15),
  // Crude Oil
  eMkt('crude-wti', 'WTI Crude Oil', 'WTI', 'oil', 'Cushing', 'US', 'us', 'NYMEX CL front month', 'EIA + Yahoo', 'CL'),
  eMkt('crude-brent', 'Brent Crude Oil', 'Brent', 'oil', 'North Sea', 'GB', 'europe', 'ICE Brent front month', 'EIA + Yahoo', 'BZ'),
  eMkt('crude-dubai', 'Dubai Crude', 'Dubai', 'oil', 'Dubai', 'AE', 'middle_east', 'Platts Dubai', 'Estimate', 'DUBAI', 2, 15),
  // Refined Products
  eMkt('en590-ara', 'EN590 Diesel ARA', 'EN590', 'product', 'ARA', 'NL', 'europe', 'ICE Gasoil proxy', 'Yahoo HO=F', 'HO', 2, 15),
  eMkt('gasoline-rbob', 'RBOB Gasoline', 'RBOB', 'product', 'USGC', 'US', 'us', 'NYMEX RBOB', 'Yahoo RB=F', 'RB', 2, 15),
  eMkt('ulsd', 'Ultra Low Sulfur Diesel', 'ULSD', 'product', 'USGC', 'US', 'us', 'NYMEX HO', 'Yahoo HO=F', 'HO'),
  // Carbon
  eMkt('carbon-eua', 'EU ETS Carbon', 'EUA', 'carbon', 'EU', 'EU', 'europe', 'ICE EUA Dec', 'Yahoo Finance', 'EUA', 2, 10),
  eMkt('carbon-uka', 'UK ETS Carbon', 'UKA', 'carbon', 'UK', 'GB', 'europe', 'ICE UKA', 'Estimate', 'UKA', 3, 10),
  // Power
  eMkt('power-de', 'German Power EEX', 'DE Power', 'power', 'Germany', 'DE', 'europe', 'EEX Phelix Base', 'EIA proxy', 'PHELIX', 2, 10),
  eMkt('power-pjm', 'PJM Day-Ahead', 'PJM', 'power', 'PJM', 'US', 'us', 'PJM Western Hub DA', 'EIA', 'PJM', 2, 10),
  eMkt('power-ercot', 'ERCOT Real-Time', 'ERCOT', 'power', 'ERCOT', 'US', 'us', 'ERCOT Hub Avg', 'EIA', 'ERCOT', 2, 10),
  // Spreads
  eMkt('spark-de', 'German Spark Spread', 'DE Spark', 'spread', 'Germany', 'DE', 'europe', 'POWER-DE - (TTF x 7.0)', 'Derived', 'SPARK', 2, 10),
  eMkt('clean-spark-de', 'Clean Spark Spread DE', 'Clean Spark', 'spread', 'Germany', 'DE', 'europe', 'SPARK-DE - (EUA x 0.37)', 'Derived', 'CSPARK', 2, 10),
  eMkt('crack-321', '3-2-1 Crack Spread', '321 Crack', 'spread', 'USGC', 'US', 'us', '(2xRBOB + HO)/3 - CL', 'Derived', 'CRACK', 2, 10),
  eMkt('basis-hh-ttf', 'HH-TTF Basis', 'HH-TTF', 'spread', 'Atlantic', 'GLOBAL', 'global', 'TTF(USD) - HH', 'Derived', 'BASIS'),
  // Freight
  eMkt('bdi', 'Baltic Dry Index', 'BDI', 'freight', 'London', 'GB', 'europe', 'Baltic Exchange BDI', 'Yahoo ^BDI', 'BDI', 3, 5),
  eMkt('freight-lng', 'LNG Freight USGC-NWE', 'LNG Frt', 'freight', 'Atlantic', 'GLOBAL', 'global', 'LNG shipping rate model', 'Sextant model', 'LNGF', 3, 5),
];


import { ALL_INDEX_MARKETS } from "./index_markets";

export const ALL_MARKETS: MarketConfig[] = [...ALL_STATIONS.flatMap(generateMarketsForStation), ...ENERGY_MARKETS, ...ALL_INDEX_MARKETS as MarketConfig[]];

// Convenience filters
export const getMarketsByPhase = (phase: LaunchPhase) => ALL_MARKETS.filter(m => m.launchPhase === phase);
export const getMarketsByType = (type: ProductType) => ALL_MARKETS.filter(m => m.type === type);
export const getMarketsByRegion = (region: Region) => ALL_MARKETS.filter(m => m.station.region === region);
export const getMarketsByCity = (city: string) => ALL_MARKETS.filter(m => m.station.city === city);
export const getPhase1Markets = () => getMarketsByPhase(1); // 30 launch markets
